<script setup>
import {ref, computed, watch, onMounted, onUnmounted, nextTick, markRaw} from 'vue'
import CodemirrorModeMenu from '@/common/codemirror/CodemirrorModeMenu'
import CodemirrorLineNumbersBtn from '@/common/codemirror/CodemirrorLineNumbersBtn'
import CodemirrorLineWrappingBtn from '@/common/codemirror/CodemirrorLineWrappingBtn'
import CodemirrorTwigModeBtn from '@/common/codemirror/CodemirrorTwigModeBtn'
import {useUserStore} from '@/stores/user'
import {useFormStore} from '@/stores/form'
import {useAppStore} from '@/stores/appStore'
import api from '@/services/api'

const props = defineProps({
    options: {type: Object, required: true}
})

const emit = defineEmits(['hide-modal'])

const appStore = useAppStore()
const userStore = useUserStore()

const codeMirrorTextArea = ref(null)

// Non-reactive reference so Vue's Proxy engine doesn't break CodeMirror's DOM tree
let cmInstance = null

const formId = props.options.formId
const formStore = useFormStore(formId)

const currentData = props.options.currentData
const field = props.options.field
const tableName = props.options.tableName
const mode = props.options.mode
const readOnly = !!props.options.readOnly
const onSaveToField = props.options.onSaveToField
const state = props.options.state

// Compute initial text reliably across props
const getInitialValue = () => {
    if (props.options.value !== undefined && props.options.value !== null) {
        return props.options.value
    }
    if (currentData && field?.name && currentData[field.name] !== undefined) {
        return currentData[field.name]
    }
    return ''
}

const currentValue = ref(getInitialValue())
const originalValue = ref(props.options.originalValue ?? getInitialValue())
const idExists = ref(!!currentData?.id)
const modalTitle = ref(props.options.modalTitle || '&nbsp;')
const showTwigControl = ref(mode === 'twig' || mode === 'text/html')

const changesExist = computed(() => originalValue.value !== currentValue.value)
const theme = computed(() => userStore.pref('codeMirror_theme'))
const lineNumbers = computed(() => userStore.pref('codeMirror_lineNumbers'))
const lineWrapping = computed(() => userStore.pref('codeMirror_lineWrapping'))
const twigMode = computed(() => userStore.pref('codeMirror_twigMode'))

const safeSetOption = (option, val) => {
    if (cmInstance && val !== undefined && val !== null) {
        cmInstance.setOption(option, val)
    }
}

watch(theme, (val) => safeSetOption('theme', val))
watch(lineNumbers, (val) => safeSetOption('lineNumbers', !!val))
watch(lineWrapping, (val) => safeSetOption('lineWrapping', !!val))
watch(twigMode, (val) => {
    let m = mode
    if (m === 'text/html' || m === 'twig') {
        m = val ? 'twig' : 'text/html'
    }
    safeSetOption('mode', m)
})

const initCodeMirror = () => {
    const targetElement = codeMirrorTextArea.value
    if (!targetElement) return

    if (cmInstance) {
        cmInstance.toTextArea()
        cmInstance = null
    }

    const initialText = getInitialValue()

    const options = {
        value: initialText,
        mode,
        lineNumbers: lineNumbers.value ?? true,
        lineWrapping: lineWrapping.value ?? false,
        readOnly: readOnly,
        indentUnit: 2,
        tabMode: 'spaces',
        enterMode: 'indent',
        electricChars: false,
        autofocus: true,
        autoCloseTags: true
    }

    if (theme.value) options.theme = theme.value

    if (twigMode.value !== undefined && (mode === 'text/html' || mode === 'twig')) {
        options.mode = twigMode.value ? 'twig' : 'text/html'
    }

    if (mode === 'javascript' || mode === 'css') {
        options.gutters = ['CodeMirror-lint-markers']
        options.lint = true
    }

    // eslint-disable-next-line no-undef
    const instance = CodeMirror.fromTextArea(targetElement, options)
    cmInstance = markRaw(instance)

    // Force value population
    cmInstance.setValue(initialText)
    cmInstance.setSize('100%', '100%')

    if (state?.history) {
        cmInstance.setHistory(state.history)
    }

    cmInstance.on('change', () => {
        currentValue.value = cmInstance.getValue()
        if (state) {
            state.history = cmInstance.getHistory()
        }
    })

    cmInstance.setOption('extraKeys', {
        Tab: (cmEditor) => {
            const spaces = Array(cmEditor.getOption('indentUnit') + 1).join(' ')
            cmEditor.replaceSelection(spaces)
        }
    })

    setTimeout(() => {
        if (cmInstance) {
            cmInstance.refresh()
            cmInstance.focus()
        }
    }, 150)
}

onMounted(() => {
    nextTick(() => {
        initCodeMirror()
    })
})

onUnmounted(() => {
    if (cmInstance) {
        cmInstance.toTextArea()
        cmInstance = null
    }
})

const saveToField = () => {
    originalValue.value = currentValue.value
    if (field.name) {
        currentData[field.name] = currentValue.value
    }
    if (onSaveToField) {
        onSaveToField({newValue: currentValue.value})
    }
}

const saveToDatabase = async () => {
    if (field.name) {
        try {
            await api.patch(`api/component/${tableName}/${currentData.id}`, {
                [field.name]: currentValue.value
            })
            appStore.cacheNeedsClearing = true
        } catch (err) {
            // Handle error if needed
        }

        formStore.presetData({
            name: field.name,
            value: currentValue.value
        })
    }
}

const discardChanges = () => {
    if (confirm('Are you sure? You have unsaved changes')) {
        emit('hide-modal')
    }
}

const saveChanges = () => {
    saveToField()
    saveToDatabase()
}

const saveChangesAndCloseModal = () => {
    saveToField()
    emit('hide-modal')
}
</script>

<template>
    <div class="modal-dialog modal-fullscreen code-mirror-modal">
        <div class="modal-content">
            <div class="modal-header d-flex py-1">
                <h4 class="modal-title flex-fill fs-6" v-html="modalTitle"/>

                <div class="d-flex align-items-center">
                    <div class="dropdown">
                        <button class="btn btn-sm btn-light" data-bs-toggle="dropdown" type="button">
                            <i class="bi-three-dots"/>
                        </button>

                        <ul class="dropdown-menu dropdown-menu-end" @click.stop>
                            <li class="dropdown-item text-center">
                                <CodemirrorModeMenu/>
                            </li>
                            <CodemirrorLineNumbersBtn/>
                            <CodemirrorLineWrappingBtn/>
                            <CodemirrorTwigModeBtn v-if="showTwigControl"/>
                        </ul>
                    </div>

                    <button
                        aria-label="Close"
                        class="btn-close ms-2"
                        type="button"
                        @click="$emit('hide-modal')"
                    ></button>
                </div>
            </div>

            <div class="modal-body p-0 h-100">
                <textarea ref="codeMirrorTextArea"></textarea>
            </div>

            <div class="modal-footer py-1">
                <button
                    v-if="!readOnly"
                    :class="{ 'btn-light': !changesExist, 'btn-warning': changesExist }"
                    :disabled="!changesExist || !idExists"
                    class="btn btn-sm d-flex align-items-center"
                    type="button"
                    @click="saveChanges"
                >
                    Save
                </button>

                <button
                    v-if="!readOnly"
                    :class="{ 'btn-light': !changesExist, 'btn-warning': changesExist }"
                    class="btn btn-sm"
                    type="button"
                    @click="saveChangesAndCloseModal"
                >
                    Close
                </button>

                <button
                    v-if="changesExist"
                    class="btn btn-light btn-sm modal-discard-changes"
                    type="button"
                    @click="discardChanges"
                >
                    Discard changes
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
:deep(.CodeMirror) {
    height: 100% !important;
}
</style>